import { useEffect, useState } from "react";
import { api, useApi } from "../api.js";
import { Notice, RefetchOverlay } from "../components.js";
import { ContentPreview, type ResponseType } from "../ContentPreview.js";

function friendlyCost(cost: number | null | undefined): string {
  if (cost === null || cost === undefined || isNaN(cost)) return "$0.00";
  if (cost === 0) return "$0.00";
  if (cost >= 1) return `$${cost.toFixed(2)}`;
  if (cost >= 0.01) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(4)}`;
}

// Reusable component that highlights non-zero costs and reduces opacity for 0 values
function CostDisplay({
  cost,
  size = 15,
  bold = true,
}: {
  cost: number | null | undefined;
  size?: number;
  bold?: boolean;
}) {
  const numericCost = cost ?? 0;
  const isZero = numericCost <= 0.00001;
  const isHigh = numericCost >= 1.0;

  return (
    <span
      style={{
        fontSize: size,
        fontWeight: bold ? (isZero ? 500 : 800) : 600,
        color: isZero ? "var(--muted)" : isHigh ? "var(--brand)" : "var(--text)",
        opacity: isZero ? 0.35 : 1,
        transition: "opacity 0.15s ease",
      }}
    >
      {friendlyCost(cost)}
    </span>
  );
}

// Reusable component that displays runs & approved count with 0 opacity reduction
function RunsDisplay({ runs, approved }: { runs: number; approved?: number }) {
  const isZeroRuns = !runs || runs <= 0;
  const hasApproved = approved !== undefined;
  const isZeroApproved = !approved || approved <= 0;

  if (isZeroRuns) {
    return <span style={{ fontSize: 13, color: "var(--muted)", opacity: 0.35 }}>0 runs</span>;
  }

  return (
    <span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{runs} runs</span>
      {hasApproved && (
        <span
          className="mini muted"
          style={{
            marginLeft: 4,
            fontSize: 11,
            color: isZeroApproved ? "var(--muted)" : "var(--green)",
            opacity: isZeroApproved ? 0.35 : 1,
            fontWeight: isZeroApproved ? 400 : 600,
          }}
        >
          ({approved} approved)
        </span>
      )}
    </span>
  );
}

type Range = "today" | "7d" | "30d" | "all";
type TopTab = "overall" | "syllabus";
type SyllabusSubView = "chapter" | "pipeline";

const RANGE_LABELS: Record<Range, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

interface CollectionSummary {
  id: number;
  name: string;
  syllabusId: string | null;
  board: string | null;
  className: string | null;
  subject: string | null;
}

interface OverallReportData {
  range: Range;
  summary: {
    totalSpend: number;
    totalRuns: number;
    totalApproved: number;
    totalSyllabuses: number;
    totalCollections: number;
    totalPipelines: number;
    totalCategories: number;
  };
  bySyllabus: {
    collectionId: number;
    syllabusId: string | null;
    name: string;
    board: string | null;
    className: string | null;
    subject: string | null;
    spend: number;
    runs: number;
    approved: number;
    percentage: number;
    models: Record<string, number>;
  }[];
  byCollection: {
    collectionId: number;
    name: string;
    board: string | null;
    className: string | null;
    subject: string | null;
    defaultModel: string;
    sourcesCount: number;
    spend: number;
    runs: number;
    approved: number;
    models: Record<string, number>;
  }[];
  byPipeline: {
    pipelineId: number;
    name: string;
    version: number;
    spend: number;
    runs: number;
    approved: number;
    percentage: number;
    collectionsCount: number;
    models: Record<string, number>;
  }[];
  byCategory: {
    categoryId: number;
    name: string;
    categoryType: string;
    spend: number;
    runs: number;
    approved: number;
    percentage: number;
    collectionsCount: number;
    models: Record<string, number>;
  }[];
  byModel: {
    model: string;
    spend: number;
    runs: number;
    percentage: number;
    inputTokens: number;
    outputTokens: number;
  }[];
}

export interface TopicWorkflowStats {
  id: number;
  name: string;
  type: "PIPELINE" | "CATEGORY";
  categoryType?: string;
  version?: number;
  spend: number;
  runs: number;
  approved: number;
  inReview: number;
  failed: number;
  status: "APPROVED" | "IN_REVIEW" | "FAILED" | "PENDING";
  models: Record<string, number>;
}

// Reusable component that displays completion badge with topics+subchapters completed vs total
function CompletionDisplay({
  completed,
  total,
  percentage,
  compact = false,
}: {
  completed: number;
  total: number;
  percentage?: number;
  compact?: boolean;
}) {
  const isZero = total === 0 || completed === 0;
  const isAllComplete = total > 0 && completed >= total;
  const pct = percentage !== undefined ? percentage : total > 0 ? Math.round((completed / total) * 100) : 0;

  if (total === 0) {
    return <span style={{ fontSize: 12, color: "var(--muted)", opacity: 0.35 }}>—</span>;
  }

  if (compact) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 4,
          background: isAllComplete ? "var(--green-bg, #ecfdf5)" : isZero ? "var(--panel)" : "var(--amber-bg, #fffbeb)",
          color: isAllComplete ? "var(--green, #059669)" : isZero ? "var(--muted)" : "var(--amber, #d97706)",
          border: `1px solid ${isAllComplete ? "var(--green, #059669)" : isZero ? "var(--line)" : "var(--amber, #d97706)"}`,
          opacity: isZero ? 0.45 : 1,
        }}
      >
        <span>{isAllComplete ? "✅" : isZero ? "⚪" : "🟡"}</span>
        <span>
          {completed}/{total} ({pct}%)
        </span>
      </span>
    );
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 12,
          fontWeight: 800,
          padding: "3px 8px",
          borderRadius: 6,
          background: isAllComplete ? "#ecfdf5" : isZero ? "var(--panel)" : "#fffbeb",
          color: isAllComplete ? "#047857" : isZero ? "var(--muted)" : "#b45309",
          border: `1px solid ${isAllComplete ? "#10b981" : isZero ? "var(--line)" : "#f59e0b"}`,
          opacity: isZero ? 0.4 : 1,
        }}
      >
        <span>{isAllComplete ? "✅" : isZero ? "⚪" : "🟡"}</span>
        <span>
          {completed} / {total} ({pct}%)
        </span>
      </span>
      {/* Mini Progress Bar */}
      <div
        style={{
          width: 70,
          height: 4,
          background: "var(--line)",
          borderRadius: 2,
          overflow: "hidden",
          opacity: isZero ? 0.3 : 1,
        }}
      >
        <div
          style={{
            width: `${Math.min(100, Math.max(0, pct))}%`,
            height: "100%",
            background: isAllComplete ? "var(--green, #10b981)" : "var(--brand, #3b82f6)",
            borderRadius: 2,
          }}
        />
      </div>
    </div>
  );
}

interface TopicStats {
  id: string;
  name: string;
  sequenceNo: number | null;
  spend: number;
  runs: number;
  approved: number;
  models: Record<string, number>;
  categories: Record<string, number>;
  pipelines: Record<string, number>;
  workflows?: TopicWorkflowStats[];
  lastRunAt: string | null;
  isCompleted?: boolean;
  missingWorkflows?: string[];
}

/** Topics and subchapters are both generation targets, each with its own workflows. */
type TargetKind = "TOPIC" | "SUBCHAPTER";

interface SubChapterStats {
  id: string;
  name: string;
  sequenceNo: number | null;
  spend: number;
  runs: number;
  approved: number;
  models: Record<string, number>;
  categories: Record<string, number>;
  pipelines: Record<string, number>;
  topics: TopicStats[];
  workflows?: TopicWorkflowStats[];
  lastRunAt?: string | null;
  totalTopics?: number;
  completedTopics?: number;
  totalItems?: number;
  completedItems?: number;
  isCompleted?: boolean;
  missingWorkflows?: string[];
}

interface ChapterStats {
  id: string;
  name: string;
  sequenceNo: number | null;
  spend: number;
  runs: number;
  approved: number;
  models: Record<string, number>;
  categories: Record<string, number>;
  pipelines: Record<string, number>;
  subchapters: SubChapterStats[];
  totalTopics?: number;
  completedTopics?: number;
  totalSubchapters?: number;
  completedSubchapters?: number;
  totalItems?: number;
  completedItems?: number;
  completionPercentage?: number;
  isCompleted?: boolean;
}

interface WorkflowItem {
  id: number;
  name: string;
  type: "PIPELINE" | "CATEGORY";
  categoryType?: string;
  version?: number;
  spend: number;
  runs: number;
  approved: number;
  models: Record<string, number>;
  chapterUsage: { chapterId: string; chapterName: string; runs: number; spend: number }[];
}

interface SyllabusReportData {
  collection: CollectionSummary;
  range: Range;
  summary: {
    totalSpend: number;
    totalRuns: number;
    totalApproved: number;
    totalChapters: number;
    totalTopics: number;
    models: Record<string, { runs: number; cost: number }>;
    workflows: Record<string, { runs: number; cost: number }>;
    completion?: {
      totalChapters: number;
      completedChapters: number;
      totalSubchapters: number;
      completedSubchapters: number;
      totalTopics: number;
      completedTopics: number;
      totalItems: number;
      completedItems: number;
      percentage: number;
      requiredWorkflowsCount: number;
      requiredWorkflows: { id: number; name: string; type: string }[];
    };
  };
  chapters: ChapterStats[];
  pipelineBreakdown: WorkflowItem[];
  categoryBreakdown: WorkflowItem[];
}

interface RunLogItem {
  id: string;
  rawId?: number;
  runType: "CATEGORY_JOB" | "PIPELINE_EXECUTION";
  title: string;
  model: string;
  cost: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  status: string;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

interface TopicHistoryResponse {
  target: { topicId: string | null; subChapterId: string | null; chapterId: string | null };
  summary: {
    totalSpend: number;
    totalRuns: number;
    totalApproved: number;
  };
  workflows: TopicWorkflowStats[];
  runs: RunLogItem[];
}

export function SyllabusReport() {
  const [activeTab, setActiveTab] = useState<TopTab>("overall");
  const [syllabusSubView, setSyllabusSubView] = useState<SyllabusSubView>("chapter");
  const [range, setRange] = useState<Range>("30d");
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);

  // Modal state for viewing run history. Topics and subchapters are both
  // generation targets with their own workflows, so the modal is keyed by the
  // kind rather than assuming a topic.
  const [activeTarget, setActiveTarget] = useState<{
    kind: TargetKind;
    id: string;
    name: string;
    spend: number;
    runs: number;
    isCompleted?: boolean;
    missingWorkflows?: string[];
  } | null>(null);
  const [topicHistory, setTopicHistory] = useState<RunLogItem[] | null>(null);
  const [topicWorkflows, setTopicWorkflows] = useState<TopicWorkflowStats[] | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Modal state for viewing deep run execution details (content, parameters, inputs, steps)
  const [inspectRun, setInspectRun] = useState<{
    run: RunLogItem;
    detail?: any;
    content?: string;
    responseType?: ResponseType;
    loading: boolean;
    error?: string;
    activeTab?: "overview" | "inputs" | "content" | "steps";
    selectedStepOrder?: number;
  } | null>(null);

  // Load list of collections
  const collectionsRes = useApi<CollectionSummary[]>("/collections");
  const collections = collectionsRes.data ?? [];

  // Auto-select first collection if not set
  useEffect(() => {
    if (!selectedCollectionId && collections.length > 0) {
      setSelectedCollectionId(collections[0].id);
    }
  }, [collections, selectedCollectionId]);

  // Overall report data
  const overallRes = useApi<OverallReportData>(`/reports/overall-spending?range=${range}`, [range]);

  // Syllabus-specific report data
  const syllabusRes = useApi<SyllabusReportData>(
    selectedCollectionId ? `/reports/syllabus-usage?collectionId=${selectedCollectionId}&range=${range}` : "",
    [selectedCollectionId, range],
  );

  const overall = overallRes.data;
  const syllabus = syllabusRes.data;

  // Open drill-down into a specific syllabus
  const handleOpenSyllabusDeepDive = (collectionId: number) => {
    setSelectedCollectionId(collectionId);
    setSelectedChapterId(null);
    setActiveTab("syllabus");
  };

  // Open the run-history modal for either kind of target. The endpoint already
  // branches on which id it is given, so the only difference is the query key.
  const handleOpenTargetHistory = async (
    kind: TargetKind,
    target: TopicStats | SubChapterStats,
  ) => {
    if (!selectedCollectionId) return;
    setActiveTarget({
      kind,
      id: target.id,
      name: target.name,
      spend: target.spend,
      runs: target.runs,
      isCompleted: target.isCompleted,
      missingWorkflows: target.missingWorkflows,
    });
    setLoadingHistory(true);
    setTopicHistory(null);
    setTopicWorkflows(target.workflows ?? null);
    try {
      const key = kind === "TOPIC" ? "topicId" : "subChapterId";
      const res = await api.get<TopicHistoryResponse>(
        `/reports/topic-history?collectionId=${selectedCollectionId}&${key}=${encodeURIComponent(target.id)}`,
      );
      setTopicHistory(res.runs);
      if (res.workflows && res.workflows.length > 0) {
        setTopicWorkflows(res.workflows);
      }
    } catch (err) {
      console.error("Failed to load target history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseTopicHistory = () => {
    setActiveTarget(null);
    setTopicHistory(null);
    setTopicWorkflows(null);
  };

  // Inspect single run details (parameters, inputs, prompts, content, step graph)
  const handleInspectRun = async (run: RunLogItem) => {
    const numericId = run.rawId ?? Number(run.id.replace(/^(job-|pipe-)/, ""));
    setInspectRun({
      run,
      loading: true,
      activeTab: "overview",
    });

    try {
      if (run.runType === "CATEGORY_JOB") {
        const detail = await api.get<any>(`/generation-jobs/${numericId}/detail`);
        let contentData: any = null;
        try {
          contentData = await api.get<any>(`/generation-jobs/${numericId}/content`);
        } catch {
          // ignore if no content generated
        }
        setInspectRun({
          run,
          detail,
          content: contentData?.content ?? detail?.content?.content ?? "",
          responseType: (contentData?.responseType as ResponseType) ?? "html",
          loading: false,
          activeTab: "overview",
        });
      } else {
        const exec = await api.get<any>(`/pipelines/executions/${numericId}`);
        const finalContent = exec?.outputContent ?? exec?.finalOutput ?? "";
        setInspectRun({
          run,
          detail: exec,
          content: typeof finalContent === "string" ? finalContent : JSON.stringify(finalContent, null, 2),
          responseType: "html",
          loading: false,
          activeTab: "overview",
          selectedStepOrder: exec?.stepExecutions?.[0]?.stepOrder ?? 1,
        });
      }
    } catch (err: any) {
      setInspectRun({
        run,
        loading: false,
        error: err.message || "Failed to load execution details",
        activeTab: "overview",
      });
    }
  };

  const handleCloseInspectRun = () => {
    setInspectRun(null);
  };

  // Export Overall summary CSV
  const handleExportOverallCSV = () => {
    if (!overall) return;
    const rows: string[][] = [
      ["Syllabus / Board / Class", "Collection Name", "Total Spend ($)", "Total Runs", "Approved Runs", "Share %", "Models Used"],
    ];

    for (const s of overall.bySyllabus) {
      const label = [s.board, s.className, s.subject].filter(Boolean).join(" - ") || s.name;
      rows.push([
        label,
        s.name,
        s.spend.toFixed(4),
        String(s.runs),
        String(s.approved),
        `${s.percentage}%`,
        Object.keys(s.models).join("; "),
      ]);
    }

    const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.map((val) => `"${val.replace(/"/g, '""')}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Overall_Spending_Report_${range}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Syllabus deep-dive CSV
  const handleExportSyllabusCSV = () => {
    if (!syllabus) return;
    const rows: string[][] = [
      ["Chapter", "Subchapter", "Topic", "Spend ($)", "Runs Count", "Approved Count", "Models Used", "Workflows/Categories"],
    ];

    for (const ch of syllabus.chapters) {
      const chModels = Object.keys(ch.models).join("; ");
      const chCats = [...Object.keys(ch.categories), ...Object.keys(ch.pipelines)].join("; ");
      rows.push([ch.name, "All Subchapters", "Entire Chapter", ch.spend.toFixed(4), String(ch.runs), String(ch.approved), chModels, chCats]);

      for (const sc of ch.subchapters) {
        for (const top of sc.topics) {
          const topModels = Object.keys(top.models).join("; ");
          const topCats = [...Object.keys(top.categories), ...Object.keys(top.pipelines)].join("; ");
          rows.push([ch.name, sc.name, top.name, top.spend.toFixed(4), String(top.runs), String(top.approved), topModels, topCats]);
        }
      }
    }

    const csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.map((val) => `"${val.replace(/"/g, '""')}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Syllabus_Report_${syllabus.collection.name}_${range}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const selectedChapter = syllabus?.chapters.find((c) => c.id === selectedChapterId);

  return (
    <div style={{ width: "100%", padding: "14px 20px 48px", boxSizing: "border-box" }}>
      {/* Top Streamlined Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          paddingBottom: 14,
          borderBottom: "1px solid var(--line)",
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22 }} role="img" aria-label="report">
              📊
            </span>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "var(--text)" }}>
              Spending & Usage Analytics Hub
            </h1>
          </div>
          <p className="mini muted" style={{ margin: "2px 0 0", fontSize: 13 }}>
            Executive spending reports across syllabuses, collections, pipelines, and categories.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Top Tab Bar Switcher */}
          <div
            style={{
              display: "flex",
              background: "var(--bg)",
              padding: 3,
              borderRadius: 8,
              border: "1px solid var(--line)",
            }}
          >
            <button
              onClick={() => setActiveTab("overall")}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                background: activeTab === "overall" ? "var(--brand)" : "transparent",
                color: activeTab === "overall" ? "#fff" : "var(--text)",
                transition: "all 0.15s ease",
              }}
            >
              🌐 Organization Overview
            </button>
            <button
              onClick={() => setActiveTab("syllabus")}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                background: activeTab === "syllabus" ? "var(--brand)" : "transparent",
                color: activeTab === "syllabus" ? "#fff" : "var(--text)",
                transition: "all 0.15s ease",
              }}
            >
              📘 Syllabus Deep-Dive
            </button>
          </div>

          {/* Time Window Selector */}
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as Range)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid var(--line)",
              background: "var(--bg)",
              color: "var(--text)",
              cursor: "pointer",
            }}
          >
            {(Object.keys(RANGE_LABELS) as Range[]).map((r) => (
              <option key={r} value={r}>
                📅 {RANGE_LABELS[r]}
              </option>
            ))}
          </select>

          {/* Download Button */}
          <button
            className="btn"
            onClick={activeTab === "overall" ? handleExportOverallCSV : handleExportSyllabusCSV}
            disabled={activeTab === "overall" ? !overall : !syllabus}
            style={{
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>📥</span> Download Excel
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: OVERALL SPENDING (ORGANIZATION-WIDE DASHBOARD)                     */}
      {/* ========================================================================= */}
      {activeTab === "overall" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {overallRes.error && <Notice kind="error">Could not load overall reports: {overallRes.error}</Notice>}
          {!overall && overallRes.loading && (
            <div style={{ padding: 24, textAlign: "center", border: "1px solid var(--line)", borderRadius: 8, background: "var(--panel)" }}>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                Loading organization spending data...
              </p>
            </div>
          )}

          {overall && (
            <RefetchOverlay loading={overallRes.loading}>
            <>
              {/* Overall KPI Summary Metric Strip */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 12,
                }}
              >
                {/* Total Spend */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid var(--brand)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Total Spend
                    </span>
                    <span style={{ fontSize: 18 }}>💰</span>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", marginTop: 2 }}>
                    <CostDisplay cost={overall.summary.totalSpend} size={26} />
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    Across {overall.summary.totalSyllabuses} Syllabuses & {overall.summary.totalPipelines} Pipelines
                  </div>
                </div>

                {/* Total Runs */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid var(--green)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Total Generations
                    </span>
                    <span style={{ fontSize: 18 }}>🔄</span>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", marginTop: 2 }}>
                    {overall.summary.totalRuns} Runs
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    <strong style={{ color: "var(--green)" }}>{overall.summary.totalApproved} Approved</strong> •{" "}
                    {overall.summary.totalRuns - overall.summary.totalApproved} in review/active
                  </div>
                </div>

                {/* Primary AI Models */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid #7e22ce",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Primary AI Models
                    </span>
                    <span style={{ fontSize: 18 }}>🤖</span>
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: "var(--text)",
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {overall.byModel.slice(0, 2).map((m) => m.model).join(", ") || "No models recorded"}
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    {overall.byModel
                      .map((m) => `${m.model} (${m.percentage}%)`)
                      .slice(0, 3)
                      .join(" · ") || "Run a generation to see model stats"}
                  </div>
                </div>
              </div>

              {/* Section 1: Spending by Syllabus (CLEAN TABLE VIEW WITHOUT UNNECESSARY COLUMNS) */}
              <div
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--panel)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--line)",
                    background: "var(--bg)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      📘 1. Spending by Syllabus
                    </h2>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                    {overall.bySyllabus.length} Syllabuses
                  </span>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr style={{ background: "var(--bg)" }}>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                          Syllabus / Collection Name
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Total Spend ($)
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Generations (Runs)
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center", width: 140 }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {overall.bySyllabus.map((syl) => {
                        const sylTitle = [syl.board, syl.className, syl.subject].filter(Boolean).join(" - ");
                        return (
                          <tr key={syl.collectionId} style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "8px 12px" }}>
                              <strong style={{ fontSize: 14, color: "var(--text)" }}>{syl.name}</strong>
                              {sylTitle && sylTitle !== syl.name && (
                                <div className="mini muted" style={{ fontSize: 12, marginTop: 1 }}>
                                  {sylTitle}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right" }}>
                              <CostDisplay cost={syl.spend} size={15} />
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              <RunsDisplay runs={syl.runs} approved={syl.approved} />
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              <button
                                className="btn mini primary"
                                onClick={() => handleOpenSyllabusDeepDive(syl.collectionId)}
                                style={{
                                  padding: "4px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  borderRadius: 6,
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                }}
                              >
                                Open Report →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 2: Spending by Pipeline (TABLE VIEW) */}
              <div
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--panel)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--line)",
                    background: "var(--bg)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      ⚙️ 2. Spending by Multi-Step Pipelines
                    </h2>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                    {overall.byPipeline.length} Pipelines
                  </span>
                </div>

                {overall.byPipeline.length === 0 ? (
                  <p className="mini muted" style={{ padding: 16, fontStyle: "italic", margin: 0 }}>
                    No pipeline executions recorded in this time window.
                  </p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                      <thead>
                        <tr style={{ background: "var(--bg)" }}>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                            Pipeline Name
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Version
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                            Total Spend ($)
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Share %
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Generations (Runs)
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Collections
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                            Models Used
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {overall.byPipeline.map((pipe) => {
                          const isZeroShare = pipe.percentage <= 0;
                          return (
                            <tr key={pipe.pipelineId} style={{ borderBottom: "1px solid var(--line)" }}>
                              <td style={{ padding: "8px 12px" }}>
                                <strong style={{ fontSize: 14, color: "var(--text)" }}>{pipe.name}</strong>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background: "#e0e7ff",
                                    color: "#4338ca",
                                  }}
                                >
                                  v{pipe.version}
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                <CostDisplay cost={pipe.spend} size={15} />
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background: isZeroShare ? "transparent" : "var(--brand2)",
                                    color: isZeroShare ? "var(--muted)" : "var(--brand)",
                                    opacity: isZeroShare ? 0.35 : 1,
                                  }}
                                >
                                  {pipe.percentage}%
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <RunsDisplay runs={pipe.runs} approved={pipe.approved} />
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                                <span style={{ opacity: pipe.collectionsCount > 0 ? 1 : 0.35 }}>
                                  {pipe.collectionsCount} collections
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)" }}>
                                {Object.keys(pipe.models).join(", ") || (
                                  <span style={{ opacity: 0.4 }}>Default</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Section 3: Spending by Content Category (TABLE VIEW) */}
              <div
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--panel)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--line)",
                    background: "var(--bg)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      📋 3. Spending by Content Category
                    </h2>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                    {overall.byCategory.length} Categories
                  </span>
                </div>

                {overall.byCategory.length === 0 ? (
                  <p className="mini muted" style={{ padding: 16, fontStyle: "italic", margin: 0 }}>
                    No category generations recorded in this time window.
                  </p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                      <thead>
                        <tr style={{ background: "var(--bg)" }}>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                            Category Name
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Type
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                            Total Spend ($)
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Share %
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Generations (Runs)
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Collections
                          </th>
                          <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                            Models Used
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {overall.byCategory.map((cat) => {
                          const isZeroShare = cat.percentage <= 0;
                          return (
                            <tr key={cat.categoryId} style={{ borderBottom: "1px solid var(--line)" }}>
                              <td style={{ padding: "8px 12px" }}>
                                <strong style={{ fontSize: 14, color: "var(--text)" }}>{cat.name}</strong>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background: "#fef3c7",
                                    color: "#b45309",
                                  }}
                                >
                                  {cat.categoryType}
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                <CostDisplay cost={cat.spend} size={15} />
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background: isZeroShare ? "transparent" : "var(--brand2)",
                                    color: isZeroShare ? "var(--muted)" : "var(--brand)",
                                    opacity: isZeroShare ? 0.35 : 1,
                                  }}
                                >
                                  {cat.percentage}%
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                <RunsDisplay runs={cat.runs} approved={cat.approved} />
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                                <span style={{ opacity: cat.collectionsCount > 0 ? 1 : 0.35 }}>
                                  {cat.collectionsCount} collections
                                </span>
                              </td>
                              <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)" }}>
                                {Object.keys(cat.models).join(", ") || (
                                  <span style={{ opacity: 0.4 }}>Default</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Section 4: Spending by Knowledge Base Collection (TABLE VIEW) */}
              <div
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--panel)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--line)",
                    background: "var(--bg)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      📁 4. Spending by Knowledge Base Collection
                    </h2>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                    {overall.byCollection.length} Collections
                  </span>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr style={{ background: "var(--bg)" }}>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                          Collection Name
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                          Academic Mapping
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          PDF Sources
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                          Default Model
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Generations
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Total Spend ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {overall.byCollection.map((c) => (
                        <tr key={c.collectionId} style={{ borderBottom: "1px solid var(--line)" }}>
                          <td style={{ padding: "8px 12px" }}>
                            <strong style={{ fontSize: 14, color: "var(--text)" }}>{c.name}</strong>
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: 13, color: "var(--muted)" }}>
                            {c.board ? `${c.board} · ${c.className ?? ""} ${c.subject ?? ""}` : "Unbound Collection"}
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13 }}>
                            <span style={{ opacity: c.sourcesCount > 0 ? 1 : 0.35 }}>
                              📄 {c.sourcesCount} files
                            </span>
                          </td>
                          <td style={{ padding: "8px 12px", fontSize: 13, color: "var(--muted)" }}>
                            🤖 {c.defaultModel}
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13 }}>
                            <RunsDisplay runs={c.runs} approved={c.approved} />
                          </td>
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>
                            <CostDisplay cost={c.spend} size={15} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 5: Spending by AI Model (TABLE VIEW) */}
              <div
                style={{
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--panel)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: "1px solid var(--line)",
                    background: "var(--bg)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                      🤖 5. Spending by AI Model
                    </h2>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                    {overall.byModel.length} Models
                  </span>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr style={{ background: "var(--bg)" }}>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                          AI Model
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Total Spend ($)
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Share %
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Total Runs
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Input Tokens
                        </th>
                        <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Output Tokens
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {overall.byModel.map((m) => {
                        const isZeroShare = m.percentage <= 0;
                        return (
                          <tr key={m.model} style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "8px 12px" }}>
                              <strong style={{ fontSize: 14, color: "var(--text)" }}>🤖 {m.model}</strong>
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right" }}>
                              <CostDisplay cost={m.spend} size={15} />
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: isZeroShare ? "transparent" : "#f3e8ff",
                                  color: isZeroShare ? "var(--muted)" : "#7e22ce",
                                  opacity: isZeroShare ? 0.35 : 1,
                                }}
                              >
                                {m.percentage}%
                              </span>
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13 }}>
                              <RunsDisplay runs={m.runs} />
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 13, color: "var(--muted)" }}>
                              <span style={{ opacity: m.inputTokens > 0 ? 1 : 0.35 }}>
                                {m.inputTokens.toLocaleString()}
                              </span>
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 13, color: "var(--muted)" }}>
                              <span style={{ opacity: m.outputTokens > 0 ? 1 : 0.35 }}>
                                {m.outputTokens.toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
            </RefetchOverlay>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: SYLLABUS DEEP-DIVE (DUAL VIEW: CHAPTER vs PIPELINE/CATEGORY)       */}
      {/* ========================================================================= */}
      {activeTab === "syllabus" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Syllabus Selector & View Mode Minimalist Bar */}
          <div
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              background: "var(--panel)",
              border: "1px solid var(--line)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {/* Syllabus Dropdown */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 320px" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>
                Syllabus:
              </span>
              <select
                value={selectedCollectionId ?? ""}
                onChange={(e) => {
                  setSelectedCollectionId(Number(e.target.value));
                  setSelectedChapterId(null);
                }}
                style={{
                  flex: "1 1 auto",
                  maxWidth: 400,
                  padding: "6px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  cursor: "pointer",
                }}
              >
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    📘 {c.name} {c.className ? `(${c.className} ${c.subject ?? ""})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Switcher (Chapter vs Pipeline & Category) */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>
                View Mode:
              </span>
              <div
                style={{
                  display: "flex",
                  background: "var(--bg)",
                  padding: 3,
                  borderRadius: 8,
                  border: "1px solid var(--line)",
                }}
              >
                <button
                  onClick={() => setSyllabusSubView("chapter")}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    background: syllabusSubView === "chapter" ? "var(--brand)" : "transparent",
                    color: syllabusSubView === "chapter" ? "#fff" : "var(--text)",
                  }}
                >
                  📖 Chapter & Topic View
                </button>
                <button
                  onClick={() => setSyllabusSubView("pipeline")}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    background: syllabusSubView === "pipeline" ? "var(--brand)" : "transparent",
                    color: syllabusSubView === "pipeline" ? "#fff" : "var(--text)",
                  }}
                >
                  ⚙️ Pipeline & Category View
                </button>
              </div>
            </div>
          </div>

          {syllabusRes.error && <Notice kind="error">Could not load syllabus report: {syllabusRes.error}</Notice>}
          {!syllabus && syllabusRes.loading && (
            <div style={{ padding: 24, textAlign: "center", border: "1px solid var(--line)", borderRadius: 8, background: "var(--panel)" }}>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                Loading syllabus report...
              </p>
            </div>
          )}

          {syllabus && (
            <RefetchOverlay loading={syllabusRes.loading}>
            <>
              {/* Syllabus KPI Summary Ribbon */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                {/* Spend Card */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid var(--brand)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Syllabus Spend
                    </span>
                    <span style={{ fontSize: 18 }}>💰</span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text)", marginTop: 2 }}>
                    <CostDisplay cost={syllabus.summary.totalSpend} size={24} />
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    Across {syllabus.summary.totalChapters} chapters & {syllabus.summary.totalTopics} topics
                  </div>
                </div>

                {/* Overall Completion Card */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: `4px solid ${
                      syllabus.summary.completion && syllabus.summary.completion.percentage === 100
                        ? "var(--green)"
                        : "var(--amber, #f59e0b)"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Overall Completion
                    </span>
                    <span style={{ fontSize: 18 }}>
                      {syllabus.summary.completion && syllabus.summary.completion.percentage === 100 ? "✅" : "🎯"}
                    </span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text)", marginTop: 2 }}>
                    {syllabus.summary.completion ? (
                      <span>
                        {syllabus.summary.completion.completedItems} / {syllabus.summary.completion.totalItems}{" "}
                        <span style={{ fontSize: 16, color: "var(--muted)", fontWeight: 700 }}>
                          ({syllabus.summary.completion.percentage}%)
                        </span>
                      </span>
                    ) : (
                      "0 / 0 (0%)"
                    )}
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    {syllabus.summary.completion ? (
                      <span>
                        <strong style={{ color: "var(--text)" }}>
                          {syllabus.summary.completion.completedChapters}/{syllabus.summary.completion.totalChapters}
                        </strong>{" "}
                        chapters ·{" "}
                        <strong style={{ color: "var(--text)" }}>
                          {syllabus.summary.completion.completedTopics}/{syllabus.summary.completion.totalTopics}
                        </strong>{" "}
                        topics (excl. Book Back)
                      </span>
                    ) : (
                      "Topics & subchapters completed"
                    )}
                  </div>
                </div>

                {/* Runs Card */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid var(--green)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Times Generated
                    </span>
                    <span style={{ fontSize: 18 }}>🔄</span>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text)", marginTop: 2 }}>
                    {syllabus.summary.totalRuns} Runs
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    <strong style={{ color: "var(--green)" }}>{syllabus.summary.totalApproved} Approved</strong> •{" "}
                    {syllabus.summary.totalRuns - syllabus.summary.totalApproved} in review/active
                  </div>
                </div>

                {/* Models Used */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: "var(--panel)",
                    border: "1px solid var(--line)",
                    borderLeft: "4px solid #7e22ce",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)" }}>
                      Models in Syllabus
                    </span>
                    <span style={{ fontSize: 18 }}>🤖</span>
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: "var(--text)",
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {Object.keys(syllabus.summary.models).length > 0
                      ? Object.keys(syllabus.summary.models).slice(0, 2).join(", ")
                      : "No models recorded"}
                  </div>
                  <div className="mini muted" style={{ marginTop: 2, fontSize: 12 }}>
                    {Object.entries(syllabus.summary.models)
                      .map(([m, st]) => `${m} (${st.runs}x)`)
                      .join(" · ") || "Run a generation to see model stats"}
                  </div>
                </div>
              </div>

              {/* DUAL SUB-VIEW 1: CHAPTER-WISE TABLE VIEW */}
              {syllabusSubView === "chapter" && (
                <div
                  style={{
                    borderRadius: 8,
                    border: "1px solid var(--line)",
                    background: "var(--panel)",
                    overflow: "hidden",
                  }}
                >
                  {/* Breadcrumb Header */}
                  <div
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid var(--line)",
                      background: "var(--bg)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700 }}>
                      {selectedChapter ? (
                        <>
                          <button
                            onClick={() => setSelectedChapterId(null)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "var(--brand)",
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: 0,
                            }}
                          >
                            ← All Chapters
                          </button>
                          <span style={{ color: "var(--muted)" }}>/</span>
                          <span style={{ color: "var(--text)" }}>{selectedChapter.name}</span>
                          <span style={{ marginLeft: 6 }}>
                            <CompletionDisplay
                              completed={selectedChapter.completedItems ?? 0}
                              total={selectedChapter.totalItems ?? 0}
                              percentage={selectedChapter.completionPercentage}
                              compact={true}
                            />
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "var(--brand)", fontSize: 15, fontWeight: 800 }}>
                          Chapters Table ({syllabus.chapters.length} Chapters)
                        </span>
                      )}
                    </div>
                    <div className="mini muted" style={{ fontSize: 12 }}>
                      {selectedChapter
                        ? "Showing subchapters & individual topic costs for this chapter."
                        : "Track topic and subchapter completion progress per chapter."}
                    </div>
                  </div>

                  {/* Level 1: All Chapters Table with Completion Column */}
                  {!selectedChapter && (
                    <div style={{ overflowX: "auto" }}>
                      {syllabus.chapters.length === 0 ? (
                        <div style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
                          No chapters found in this syllabus.
                        </div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                          <thead>
                            <tr style={{ background: "var(--bg)" }}>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center", width: 60 }}>
                                #
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Chapter Name
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Subchapters
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Completion (Topics + Subchapters)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                                Total Spend ($)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Generations (Runs)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center", width: 140 }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {syllabus.chapters.map((ch, idx) => (
                              <tr key={ch.id || idx} style={{ borderBottom: "1px solid var(--line)" }}>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 800,
                                      background: "var(--brand2)",
                                      color: "var(--brand)",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    Ch {ch.sequenceNo ?? idx + 1}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 12px" }}>
                                  <strong style={{ fontSize: 14, color: "var(--text)" }}>{ch.name}</strong>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
                                  <span style={{ opacity: ch.subchapters.length > 0 ? 1 : 0.35 }}>
                                    {ch.subchapters.length} subchapters
                                  </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <CompletionDisplay
                                    completed={ch.completedItems ?? 0}
                                    total={ch.totalItems ?? 0}
                                    percentage={ch.completionPercentage}
                                  />
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                  <CostDisplay cost={ch.spend} size={15} />
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <RunsDisplay runs={ch.runs} approved={ch.approved} />
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <button
                                    className="btn mini primary"
                                    onClick={() => setSelectedChapterId(ch.id)}
                                    style={{
                                      padding: "4px 10px",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      borderRadius: 6,
                                      whiteSpace: "nowrap",
                                      cursor: "pointer",
                                    }}
                                  >
                                    View Topics →
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* Level 2: Subchapters & Topics Drill-down */}
                  {selectedChapter && (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {selectedChapter.subchapters.length === 0 && (
                        <div style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
                          No subchapters listed under this chapter.
                        </div>
                      )}

                      {selectedChapter.subchapters.map((sc, scIdx) => (
                        <div key={sc.id || scIdx} style={{ borderBottom: "1px solid var(--line)" }}>
                          {/* Subchapter Header Bar */}
                          <div
                            style={{
                              padding: "8px 14px",
                              background: "var(--bg)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderBottom: "1px solid var(--line)",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", color: "var(--muted)" }}>
                                Subchapter {sc.sequenceNo ?? scIdx + 1}:
                              </span>
                              <strong style={{ fontSize: 14, color: "var(--text)" }}>{sc.name}</strong>
                              <span
                                title="Topics under this subchapter that are complete"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                              >
                                <span style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>
                                  Topics
                                </span>
                                <CompletionDisplay
                                  completed={sc.completedTopics ?? 0}
                                  total={sc.totalTopics ?? sc.topics.length}
                                  compact={true}
                                />
                              </span>
                              {/* The subchapter's own workflows are a separate
                                  question from its topics': it has pipelines and
                                  categories of its own, so "all topics done" is
                                  not the same as "this subchapter is done". */}
                              <span
                                title={
                                  sc.missingWorkflows && sc.missingWorkflows.length > 0
                                    ? `This subchapter's own missing workflows: ${sc.missingWorkflows.join(", ")}`
                                    : "This subchapter's own required workflows are all approved"
                                }
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  background: sc.isCompleted ? "var(--green-bg, #ecfdf5)" : "var(--panel)",
                                  color: sc.isCompleted ? "var(--green, #059669)" : "var(--muted)",
                                  border: `1px solid ${sc.isCompleted ? "var(--green, #059669)" : "var(--line)"}`,
                                }}
                              >
                                <span>{sc.isCompleted ? "✅" : "⚪"}</span>
                                <span>
                                  Own{" "}
                                  {sc.missingWorkflows && sc.missingWorkflows.length > 0
                                    ? `${sc.missingWorkflows.length} missing`
                                    : sc.isCompleted
                                      ? "complete"
                                      : "pending"}
                                </span>
                              </span>
                            </div>
                            <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 12 }}>
                              <CostDisplay cost={sc.spend} size={13} />
                              <RunsDisplay runs={sc.runs} approved={sc.approved} />
                              <button
                                className="btn mini"
                                onClick={() => handleOpenTargetHistory("SUBCHAPTER", sc)}
                                title={
                                  sc.missingWorkflows && sc.missingWorkflows.length > 0
                                    ? `Missing approved workflows: ${sc.missingWorkflows.join(", ")}`
                                    : "View this subchapter's own run history"
                                }
                                style={{
                                  padding: "3px 8px",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  borderRadius: 4,
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                <span>🔍</span> Log
                              </button>
                            </div>
                          </div>

                          {/* Topics Table under this subchapter */}
                          {sc.topics.length === 0 ? (
                            <div className="mini muted" style={{ padding: "10px 14px", fontStyle: "italic", fontSize: 12 }}>
                              No individual topics under this subchapter.
                            </div>
                          ) : (
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                                <thead>
                                  <tr style={{ background: "var(--panel)" }}>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                                      Topic Name
                                    </th>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                      Status
                                    </th>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                                      Spend ($)
                                    </th>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                      Generations
                                    </th>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                                      Models
                                    </th>
                                    <th style={{ padding: "6px 12px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center", width: 90 }}>
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sc.topics.map((topic, tIdx) => {
                                    const topModels = Object.keys(topic.models);
                                    const isZeroTopicRuns = topic.runs <= 0;
                                    const isCompleted = topic.isCompleted ?? false;
                                    const totalRequired =
                                      syllabus?.summary?.completion?.requiredWorkflowsCount ??
                                      (syllabus?.summary?.completion?.requiredWorkflows?.length || 0);
                                    const missingCount = topic.missingWorkflows?.length ?? 0;
                                    const completedCount = Math.max(0, totalRequired - missingCount);

                                    return (
                                      <tr key={topic.id || tIdx} style={{ borderBottom: "1px solid var(--line)" }}>
                                        <td style={{ padding: "8px 12px" }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <span
                                              style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                background: isCompleted
                                                  ? "var(--green, #10b981)"
                                                  : isZeroTopicRuns
                                                  ? "var(--line)"
                                                  : "var(--amber, #f59e0b)",
                                                opacity: isZeroTopicRuns ? 0.4 : 1,
                                                display: "inline-block",
                                                flexShrink: 0,
                                              }}
                                            />
                                            <strong style={{ fontSize: 13, color: "var(--text)" }}>{topic.name}</strong>
                                          </div>
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                          {isCompleted ? (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                background: "#ecfdf5",
                                                color: "#047857",
                                                border: "1px solid #10b981",
                                              }}
                                            >
                                              ✅ Completed
                                            </span>
                                          ) : isZeroTopicRuns ? (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                padding: "2px 6px",
                                                borderRadius: 4,
                                                background: "var(--panel)",
                                                color: "var(--muted)",
                                                opacity: 0.35,
                                              }}
                                            >
                                              ⚪ Not Started
                                            </span>
                                          ) : (
                                            <span
                                              title={
                                                topic.missingWorkflows && topic.missingWorkflows.length > 0
                                                  ? `Missing approved workflows: ${topic.missingWorkflows.join(", ")} (Click "Log" for detailed checklist)`
                                                  : "Pending required approvals"
                                              }
                                              style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                                background: "#fffbeb",
                                                color: "#b45309",
                                                border: "1px solid #f59e0b",
                                                cursor: "help",
                                              }}
                                            >
                                              🟡 Incomplete{" "}
                                              {totalRequired > 0
                                                ? `(${completedCount}/${totalRequired})`
                                                : missingCount > 0
                                                ? `(${missingCount} missing)`
                                                : ""}
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                          <CostDisplay cost={topic.spend} size={14} />
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                          <RunsDisplay runs={topic.runs} approved={topic.approved} />
                                        </td>
                                        <td style={{ padding: "8px 12px", fontSize: 11, color: "var(--muted)" }}>
                                          {topModels.length > 0 ? (
                                            topModels.join(", ")
                                          ) : (
                                            <span style={{ opacity: 0.35 }}>Default</span>
                                          )}
                                        </td>
                                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                          <button
                                            className="btn mini"
                                            onClick={() => handleOpenTargetHistory("TOPIC", topic)}
                                            style={{
                                              padding: "3px 8px",
                                              fontSize: 11,
                                              fontWeight: 600,
                                              borderRadius: 4,
                                              whiteSpace: "nowrap",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: 3,
                                            }}
                                          >
                                            <span>🔍</span> Log
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* DUAL SUB-VIEW 2: PIPELINE & CATEGORY-WISE TABLE VIEW */}
              {syllabusSubView === "pipeline" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Pipelines Table */}
                  <div
                    style={{
                      borderRadius: 8,
                      border: "1px solid var(--line)",
                      background: "var(--panel)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 16px",
                        borderBottom: "1px solid var(--line)",
                        background: "var(--bg)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                          ⚙️ Multi-Step Pipelines in this Syllabus
                        </h3>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                        {syllabus.pipelineBreakdown.length} Pipelines Active
                      </span>
                    </div>

                    {syllabus.pipelineBreakdown.length === 0 ? (
                      <p className="mini muted" style={{ padding: 16, fontStyle: "italic", margin: 0 }}>
                        No pipelines executed for this syllabus in the chosen date range.
                      </p>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                          <thead>
                            <tr style={{ background: "var(--bg)" }}>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Pipeline Name
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Version
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                                Total Spend ($)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Generations (Runs)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Primary Models
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Chapter Usage
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {syllabus.pipelineBreakdown.map((pipe) => (
                              <tr key={pipe.id} style={{ borderBottom: "1px solid var(--line)" }}>
                                <td style={{ padding: "8px 12px" }}>
                                  <strong style={{ fontSize: 14, color: "var(--text)" }}>{pipe.name}</strong>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 800,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      background: "#e0e7ff",
                                      color: "#4338ca",
                                    }}
                                  >
                                    v{pipe.version ?? 1}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                  <CostDisplay cost={pipe.spend} size={15} />
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <RunsDisplay runs={pipe.runs} approved={pipe.approved} />
                                </td>
                                <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)" }}>
                                  {Object.keys(pipe.models).join(", ") || (
                                    <span style={{ opacity: 0.4 }}>Default</span>
                                  )}
                                </td>
                                <td style={{ padding: "8px 12px" }}>
                                  {pipe.chapterUsage.length === 0 ? (
                                    <span className="mini muted" style={{ fontSize: 12, opacity: 0.4 }}>None</span>
                                  ) : (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                      {pipe.chapterUsage.map((u) => (
                                        <span
                                          key={u.chapterId}
                                          style={{
                                            fontSize: 11,
                                            background: "var(--bg)",
                                            border: "1px solid var(--line)",
                                            padding: "2px 6px",
                                            borderRadius: 4,
                                            color: "var(--text)",
                                          }}
                                        >
                                          📖 {u.chapterName}: <strong>{u.runs}x</strong> ({friendlyCost(u.spend)})
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Content Categories Table */}
                  <div
                    style={{
                      borderRadius: 8,
                      border: "1px solid var(--line)",
                      background: "var(--panel)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 16px",
                        borderBottom: "1px solid var(--line)",
                        background: "var(--bg)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
                          📋 Content Categories in this Syllabus
                        </h3>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                        {syllabus.categoryBreakdown.length} Categories Active
                      </span>
                    </div>

                    {syllabus.categoryBreakdown.length === 0 ? (
                      <p className="mini muted" style={{ padding: 16, fontStyle: "italic", margin: 0 }}>
                        No category generation runs recorded in this time window.
                      </p>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                          <thead>
                            <tr style={{ background: "var(--bg)" }}>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Category Name
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Type
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                                Total Spend ($)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Generations (Runs)
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Primary Models
                              </th>
                              <th style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>
                                Chapter Usage
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {syllabus.categoryBreakdown.map((cat) => (
                              <tr key={cat.id} style={{ borderBottom: "1px solid var(--line)" }}>
                                <td style={{ padding: "8px 12px" }}>
                                  <strong style={{ fontSize: 14, color: "var(--text)" }}>{cat.name}</strong>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 800,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      background: "#fef3c7",
                                      color: "#b45309",
                                    }}
                                  >
                                    {cat.categoryType ?? "CATEGORY"}
                                  </span>
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "right" }}>
                                  <CostDisplay cost={cat.spend} size={15} />
                                </td>
                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                  <RunsDisplay runs={cat.runs} approved={cat.approved} />
                                </td>
                                <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)" }}>
                                  {Object.keys(cat.models).join(", ") || (
                                    <span style={{ opacity: 0.4 }}>Default</span>
                                  )}
                                </td>
                                <td style={{ padding: "8px 12px" }}>
                                  {cat.chapterUsage.length === 0 ? (
                                    <span className="mini muted" style={{ fontSize: 12, opacity: 0.4 }}>None</span>
                                  ) : (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                      {cat.chapterUsage.map((u) => (
                                        <span
                                          key={u.chapterId}
                                          style={{
                                            fontSize: 11,
                                            background: "var(--bg)",
                                            border: "1px solid var(--line)",
                                            padding: "2px 6px",
                                            borderRadius: 4,
                                            color: "var(--text)",
                                          }}
                                        >
                                          📖 {u.chapterName}: <strong>{u.runs}x</strong> ({friendlyCost(u.spend)})
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
            </RefetchOverlay>
          )}
        </div>
      )}

      {/* Target Run History Modal (WIDESCREEN TABLE VIEW) */}
      {activeTarget && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(3px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={handleCloseTopicHistory}
        >
          <div
            style={{
              maxWidth: 1080,
              width: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "20px 24px",
              borderRadius: 12,
              background: "var(--panel)",
              border: "1px solid var(--line)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                paddingBottom: 12,
                borderBottom: "1px solid var(--line)",
                marginBottom: 14,
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "var(--brand)",
                    background: "var(--brand2)",
                    padding: "2px 8px",
                    borderRadius: 4,
                  }}
                >
                  {activeTarget.kind === "TOPIC" ? "Topic" : "Subchapter"} Deep-Dive
                </span>
                <h2 style={{ margin: "4px 0 2px", fontSize: 18, fontWeight: 800, color: "var(--text)" }}>
                  {activeTarget.name}
                </h2>
                <p className="mini muted" style={{ margin: 0, fontSize: 13 }}>
                  Total Spent: <strong style={{ color: "var(--text)" }}>{friendlyCost(activeTarget.spend)}</strong> • Generated{" "}
                  <strong style={{ color: "var(--text)" }}>{activeTarget.runs} times</strong>
                </p>
              </div>

              <button
                onClick={handleCloseTopicHistory}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                  fontWeight: 300,
                  cursor: "pointer",
                  color: "var(--muted)",
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* Workflow breakdown for this target (TABLE VIEW including Not Generated required items) */}
            {(() => {
              const allRequired = syllabus?.summary?.completion?.requiredWorkflows ?? [];
              const existingWfMap = new Map<string, TopicWorkflowStats>();
              for (const wf of topicWorkflows ?? []) {
                existingWfMap.set(`${wf.type}:${wf.id}`, wf);
                existingWfMap.set(wf.name, wf);
              }

              const mergedWorkflows: {
                id: number;
                name: string;
                type: "PIPELINE" | "CATEGORY";
                categoryType?: string;
                version?: number;
                spend: number;
                runs: number;
                approved: number;
                inReview: number;
                failed: number;
                status: "APPROVED" | "IN_REVIEW" | "FAILED" | "NOT_GENERATED";
                isRequired: boolean;
              }[] = [];

              // 1. Add all required workflows for this collection
              for (const req of allRequired) {
                const existing = existingWfMap.get(`${req.type}:${req.id}`) || existingWfMap.get(req.name);
                if (existing) {
                  mergedWorkflows.push({
                    id: existing.id,
                    name: existing.name,
                    type: existing.type,
                    categoryType: existing.categoryType,
                    version: existing.version,
                    spend: existing.spend,
                    runs: existing.runs,
                    approved: existing.approved,
                    inReview: existing.inReview,
                    failed: existing.failed,
                    status:
                      existing.approved > 0
                        ? "APPROVED"
                        : existing.failed > 0
                        ? "FAILED"
                        : existing.runs > 0
                        ? "IN_REVIEW"
                        : "NOT_GENERATED",
                    isRequired: true,
                  });
                } else {
                  mergedWorkflows.push({
                    id: req.id,
                    name: req.name,
                    type: req.type as any,
                    spend: 0,
                    runs: 0,
                    approved: 0,
                    inReview: 0,
                    failed: 0,
                    status: "NOT_GENERATED",
                    isRequired: true,
                  });
                }
              }

              // 2. Add any other workflows run for this target that are not in requiredWorkflows
              for (const wf of topicWorkflows ?? []) {
                const alreadyAdded = mergedWorkflows.some((m) => m.name === wf.name || (m.id === wf.id && m.type === wf.type));
                if (!alreadyAdded) {
                  mergedWorkflows.push({
                    id: wf.id,
                    name: wf.name,
                    type: wf.type,
                    categoryType: wf.categoryType,
                    version: wf.version,
                    spend: wf.spend,
                    runs: wf.runs,
                    approved: wf.approved,
                    inReview: wf.inReview,
                    failed: wf.failed,
                    status: wf.approved > 0 ? "APPROVED" : wf.failed > 0 ? "FAILED" : "IN_REVIEW",
                    isRequired: false,
                  });
                }
              }

              if (mergedWorkflows.length === 0) return null;

              const reqCount = mergedWorkflows.filter((w) => w.isRequired).length;
              const approvedReqCount = mergedWorkflows.filter((w) => w.isRequired && w.status === "APPROVED").length;

              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: "var(--muted)" }}>
                      Category & Pipeline Breakdown
                    </div>
                    {reqCount > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: approvedReqCount === reqCount ? "var(--green)" : "var(--amber)",
                        }}
                      >
                        {approvedReqCount === reqCount ? "✅" : "🟡"} {approvedReqCount} of {reqCount} Required Workflows Approved
                      </span>
                    )}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                      <thead>
                        <tr style={{ background: "var(--bg)" }}>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                            Workflow / Category Name
                          </th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Type
                          </th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                            Spend ($)
                          </th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Runs
                          </th>
                          <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                            Approval Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {mergedWorkflows.map((wf) => {
                          const isNotGen = wf.status === "NOT_GENERATED";
                          return (
                            <tr key={`${wf.type}-${wf.id || wf.name}`} style={{ borderBottom: "1px solid var(--line)" }}>
                              <td style={{ padding: "6px 10px" }}>
                                <strong style={{ fontSize: 13, color: isNotGen ? "var(--muted)" : "var(--text)", opacity: isNotGen ? 0.7 : 1 }}>
                                  {wf.name}
                                </strong>
                                {wf.isRequired && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "var(--brand)",
                                      background: "var(--brand2)",
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                    }}
                                  >
                                    Required
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "6px 10px", textAlign: "center" }}>
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background: wf.type === "PIPELINE" ? "#e0e7ff" : "#fef3c7",
                                    color: wf.type === "PIPELINE" ? "#4338ca" : "#b45309",
                                    opacity: isNotGen ? 0.5 : 1,
                                  }}
                                >
                                  {wf.type === "PIPELINE" ? `Pipeline v${wf.version ?? 1}` : (wf.categoryType ?? "CATEGORY")}
                                </span>
                              </td>
                              <td style={{ padding: "6px 10px", textAlign: "right" }}>
                                <CostDisplay cost={wf.spend} size={13} />
                              </td>
                              <td style={{ padding: "6px 10px", textAlign: "center", fontSize: 12 }}>
                                <RunsDisplay runs={wf.runs} />
                              </td>
                              <td style={{ padding: "6px 10px", textAlign: "center" }}>
                                {wf.status === "APPROVED" ? (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      background: "#ecfdf5",
                                      color: "#047857",
                                      border: "1px solid #10b981",
                                    }}
                                  >
                                    ✅ {wf.approved} Approved
                                  </span>
                                ) : wf.status === "FAILED" ? (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      background: "#fef2f2",
                                      color: "#b91c1c",
                                      border: "1px solid #f87171",
                                    }}
                                  >
                                    ❌ {wf.failed} Failed
                                  </span>
                                ) : wf.status === "IN_REVIEW" ? (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      background: "#fffbeb",
                                      color: "#b45309",
                                      border: "1px solid #f59e0b",
                                    }}
                                  >
                                    🟡 {wf.runs} In Review
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      padding: "2px 8px",
                                      borderRadius: 4,
                                      background: "var(--panel)",
                                      color: "var(--muted)",
                                      opacity: 0.5,
                                      border: "1px dashed var(--line)",
                                    }}
                                  >
                                    ⚪ Not Generated
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Modal Body: Chronological Runs Log Table */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>
                Chronological Run History Log
              </div>

              {loadingHistory && (
                <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                  Loading run history for this {activeTarget.kind === "TOPIC" ? "topic" : "subchapter"}...
                </div>
              )}

              {!loadingHistory && topicHistory && topicHistory.length === 0 && (
                <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                  No past runs recorded for this {activeTarget.kind === "TOPIC" ? "topic" : "subchapter"}.
                </div>
              )}

              {!loadingHistory && topicHistory && topicHistory.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr style={{ background: "var(--bg)" }}>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                          Date & Time
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                          Run Title / Action
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
                          AI Model
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Tokens (In / Out)
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>
                          Cost ($)
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Status
                        </th>
                        <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topicHistory.map((run, idx) => {
                        const isApproved = run.status === "APPROVED";
                        const isFailed = run.status === "FAILED";
                        return (
                          <tr key={run.id || idx} style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 10px", fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
                              {new Date(run.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td style={{ padding: "6px 10px" }}>
                              <strong style={{ fontSize: 13, color: "var(--text)" }}>{run.title}</strong>
                              {run.errorMessage && (
                                <div style={{ fontSize: 11, color: "var(--red)", marginTop: 2 }}>
                                  ⚠️ {run.errorMessage}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "6px 10px", fontSize: 12, color: "var(--muted)" }}>
                              🤖 {run.model}
                            </td>
                            <td style={{ padding: "6px 10px", textAlign: "right", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                              <span style={{ opacity: run.inputTokens > 0 ? 1 : 0.35 }}>
                                {run.inputTokens.toLocaleString()}
                              </span>{" "}
                              /{" "}
                              <span style={{ opacity: run.outputTokens > 0 ? 1 : 0.35 }}>
                                {run.outputTokens.toLocaleString()}
                              </span>
                            </td>
                            <td style={{ padding: "6px 10px", textAlign: "right" }}>
                              <CostDisplay cost={run.cost} size={13} />
                            </td>
                            <td style={{ padding: "6px 10px", textAlign: "center" }}>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: isApproved ? "var(--greenbg)" : isFailed ? "var(--redbg)" : "var(--amberbg)",
                                  color: isApproved ? "var(--green)" : isFailed ? "var(--red)" : "var(--amber)",
                                }}
                              >
                                {isApproved ? "✅ Approved" : isFailed ? "❌ Failed" : `🟡 ${run.status}`}
                              </span>
                            </td>
                            <td style={{ padding: "6px 10px", textAlign: "center" }}>
                              <button
                                className="btn mini primary"
                                onClick={() => handleInspectRun(run)}
                                style={{
                                  padding: "3px 8px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  borderRadius: 4,
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                <span>🔍</span> Inspect
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 10,
                borderTop: "1px solid var(--line)",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn primary"
                onClick={handleCloseTopicHistory}
                style={{ padding: "6px 18px", fontSize: 13, fontWeight: 700, borderRadius: 6, cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deep Run Inspection Modal (Content, Parameters, Inputs, Prompts, Steps) */}
      {inspectRun && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.75)",
            backdropFilter: "blur(4px)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={handleCloseInspectRun}
        >
          <div
            style={{
              maxWidth: 1180,
              width: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              padding: "20px 24px",
              borderRadius: 12,
              background: "var(--panel)",
              border: "1px solid var(--line)",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                paddingBottom: 12,
                borderBottom: "1px solid var(--line)",
                marginBottom: 14,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      background: inspectRun.run.runType === "PIPELINE_EXECUTION" ? "#e0e7ff" : "#fef3c7",
                      color: inspectRun.run.runType === "PIPELINE_EXECUTION" ? "#4338ca" : "#b45309",
                      padding: "2px 8px",
                      borderRadius: 4,
                    }}
                  >
                    {inspectRun.run.runType === "PIPELINE_EXECUTION" ? "⚙️ Pipeline Execution" : "📋 Category Job"}
                  </span>
                  <span className="mini muted" style={{ fontSize: 12 }}>
                    ID: <strong>{inspectRun.run.id}</strong>
                  </span>
                </div>
                <h2 style={{ margin: "4px 0 2px", fontSize: 18, fontWeight: 800, color: "var(--text)" }}>
                  {inspectRun.run.title}
                </h2>
                <p className="mini muted" style={{ margin: 0, fontSize: 13 }}>
                  Incurred Cost: <strong>{friendlyCost(inspectRun.run.cost)}</strong> • Model:{" "}
                  <strong>{inspectRun.run.model}</strong> • Executed on{" "}
                  {new Date(inspectRun.run.createdAt).toLocaleString()}
                </p>
              </div>

              <button
                onClick={handleCloseInspectRun}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                  fontWeight: 300,
                  cursor: "pointer",
                  color: "var(--muted)",
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {inspectRun.loading && (
              <div style={{ padding: "32px 0", textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
                Loading full execution details, parameters, prompts, and content...
              </div>
            )}

            {inspectRun.error && (
              <Notice kind="error" style={{ marginBottom: 14 }}>
                {inspectRun.error}
              </Notice>
            )}

            {!inspectRun.loading && !inspectRun.error && (
              <div>
                {/* Modal Tab Switcher */}
                <div
                  style={{
                    display: "flex",
                    background: "var(--bg)",
                    padding: 3,
                    borderRadius: 8,
                    border: "1px solid var(--line)",
                    marginBottom: 14,
                    gap: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => setInspectRun({ ...inspectRun, activeTab: "overview" })}
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 6,
                      border: "none",
                      cursor: "pointer",
                      background: (inspectRun.activeTab ?? "overview") === "overview" ? "var(--brand)" : "transparent",
                      color: (inspectRun.activeTab ?? "overview") === "overview" ? "#fff" : "var(--text)",
                    }}
                  >
                    📊 Overview & Parameters
                  </button>
                  <button
                    onClick={() => setInspectRun({ ...inspectRun, activeTab: "inputs" })}
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 6,
                      border: "none",
                      cursor: "pointer",
                      background: inspectRun.activeTab === "inputs" ? "var(--brand)" : "transparent",
                      color: inspectRun.activeTab === "inputs" ? "#fff" : "var(--text)",
                    }}
                  >
                    📝 Inputs & Prompts
                  </button>
                  <button
                    onClick={() => setInspectRun({ ...inspectRun, activeTab: "content" })}
                    style={{
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 6,
                      border: "none",
                      cursor: "pointer",
                      background: inspectRun.activeTab === "content" ? "var(--brand)" : "transparent",
                      color: inspectRun.activeTab === "content" ? "#fff" : "var(--text)",
                    }}
                  >
                    📄 Generated Content Output
                  </button>
                  {inspectRun.run.runType === "PIPELINE_EXECUTION" && (
                    <button
                      onClick={() => setInspectRun({ ...inspectRun, activeTab: "steps" })}
                      style={{
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: 6,
                        border: "none",
                        cursor: "pointer",
                        background: inspectRun.activeTab === "steps" ? "var(--brand)" : "transparent",
                        color: inspectRun.activeTab === "steps" ? "#fff" : "var(--text)",
                      }}
                    >
                      ⚙️ Pipeline Step Graph ({inspectRun.detail?.stepExecutions?.length ?? 0})
                    </button>
                  )}
                </div>

                {/* TAB 1: OVERVIEW & PARAMETERS */}
                {(inspectRun.activeTab ?? "overview") === "overview" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Key-Value Summary Table */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "var(--muted)",
                        }}
                      >
                        Execution Summary
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <tbody>
                          <tr style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Run Status</td>
                            <td style={{ padding: "6px 0", textAlign: "right", fontWeight: 700 }}>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background:
                                    inspectRun.run.status === "APPROVED"
                                      ? "var(--greenbg)"
                                      : inspectRun.run.status === "FAILED"
                                      ? "var(--redbg)"
                                      : "var(--amberbg)",
                                  color:
                                    inspectRun.run.status === "APPROVED"
                                      ? "var(--green)"
                                      : inspectRun.run.status === "FAILED"
                                      ? "var(--red)"
                                      : "var(--amber)",
                                }}
                              >
                                {inspectRun.run.status}
                              </span>
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Total Incurred Cost</td>
                            <td style={{ padding: "6px 0", textAlign: "right", fontWeight: 800, color: "var(--text)" }}>
                              <CostDisplay cost={inspectRun.run.cost} size={14} />
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Model Executed</td>
                            <td style={{ padding: "6px 0", textAlign: "right", fontWeight: 600 }}>
                              🤖 {inspectRun.detail?.model ?? inspectRun.run.model}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Token Consumption</td>
                            <td style={{ padding: "6px 0", textAlign: "right", fontWeight: 600 }}>
                              {inspectRun.run.inputTokens.toLocaleString()} input / {inspectRun.run.outputTokens.toLocaleString()} output
                              {inspectRun.run.cachedTokens > 0 && ` (${inspectRun.run.cachedTokens.toLocaleString()} cached)`}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid var(--line)" }}>
                            <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Created At</td>
                            <td style={{ padding: "6px 0", textAlign: "right", color: "var(--text)" }}>
                              {new Date(inspectRun.run.createdAt).toLocaleString()}
                            </td>
                          </tr>
                          {inspectRun.run.completedAt && (
                            <tr style={{ borderBottom: "1px solid var(--line)" }}>
                              <td style={{ padding: "6px 0", color: "var(--muted)", fontWeight: 700 }}>Completed At</td>
                              <td style={{ padding: "6px 0", textAlign: "right", color: "var(--text)" }}>
                                {new Date(inspectRun.run.completedAt).toLocaleString()}
                              </td>
                            </tr>
                          )}
                          {inspectRun.run.errorMessage && (
                            <tr>
                              <td style={{ padding: "6px 0", color: "var(--red)", fontWeight: 700 }}>Failure Reason</td>
                              <td style={{ padding: "6px 0", textAlign: "right", color: "var(--red)", fontWeight: 600 }}>
                                {inspectRun.run.errorMessage}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Parameters Grid */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "var(--muted)",
                        }}
                      >
                        Model Generation Parameters
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Temperature</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.temperature ?? "0.7 (Default)"}</strong>
                        </div>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Top P</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.topP ?? "1.0 (Default)"}</strong>
                        </div>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Frequency Penalty</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.frequencyPenalty ?? "0.0"}</strong>
                        </div>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Presence Penalty</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.presencePenalty ?? "0.0"}</strong>
                        </div>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Max Tokens</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.maxTokens ?? "Auto / Default"}</strong>
                        </div>
                        <div style={{ padding: 8, background: "var(--panel)", borderRadius: 6, border: "1px solid var(--line)" }}>
                          <span className="mini muted" style={{ display: "block", fontSize: 11 }}>Reasoning Effort</span>
                          <strong style={{ fontSize: 13 }}>{inspectRun.detail?.parameters?.reasoningEffort ?? "Unset"}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Grounding & Knowledge Sources */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "var(--muted)",
                        }}
                      >
                        Knowledge Grounding & Vector Search
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span className="mini muted" style={{ fontSize: 12 }}>
                          📄 Chunks Retrieved: <strong>{inspectRun.detail?.content?.retrievedChunks ?? inspectRun.detail?.retrievedChunks ?? 0} text chunks</strong>
                        </span>
                        <span className="mini muted" style={{ fontSize: 12 }}>
                          🔍 Search Calls: <strong>{inspectRun.detail?.searchCalls ?? 0} vector calls</strong>
                        </span>
                      </div>
                      {inspectRun.detail?.knowledgeSources && inspectRun.detail.knowledgeSources.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                          {inspectRun.detail.knowledgeSources.map((ks: any) => (
                            <span
                              key={ks.id}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                background: "var(--panel)",
                                border: "1px solid var(--line)",
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}
                            >
                              📘 {ks.filename}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="mini muted" style={{ margin: "2px 0 0", fontStyle: "italic", fontSize: 12, opacity: 0.5 }}>
                          No direct PDF sources attached for this run.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* TAB 2: INPUTS & PROMPTS */}
                {inspectRun.activeTab === "inputs" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Target & Resolved Inputs */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "var(--muted)",
                        }}
                      >
                        Target & Resolved Inputs
                      </div>
                      <pre
                        style={{
                          background: "var(--panel)",
                          padding: 10,
                          borderRadius: 6,
                          border: "1px solid var(--line)",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 200,
                          overflowY: "auto",
                          margin: 0,
                        }}
                      >
                        {inspectRun.detail?.resolvedInputs
                          ? JSON.stringify(inspectRun.detail.resolvedInputs, null, 2)
                          : inspectRun.detail?.targetName
                          ? `Target: ${inspectRun.detail.targetName} (ID: ${inspectRun.detail.topicId || inspectRun.detail.subChapterId || "—"})`
                          : "No structured resolved inputs recorded."}
                      </pre>
                    </div>

                    {/* Prompt Snapshot */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div
                          style={{
                            margin: 0,
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            color: "var(--muted)",
                          }}
                        >
                          Prompt Snapshot
                        </div>
                        {inspectRun.detail?.promptSnapshot && (
                          <button
                            className="btn mini"
                            onClick={() => {
                              navigator.clipboard.writeText(inspectRun.detail.promptSnapshot);
                              alert("Prompt copied to clipboard!");
                            }}
                          >
                            📋 Copy Prompt
                          </button>
                        )}
                      </div>
                      <pre
                        style={{
                          background: "var(--panel)",
                          padding: 10,
                          borderRadius: 6,
                          border: "1px solid var(--line)",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 280,
                          overflowY: "auto",
                          margin: 0,
                        }}
                      >
                        {inspectRun.detail?.promptSnapshot || "No prompt snapshot recorded for this run."}
                      </pre>
                    </div>

                    {/* Sample Snapshot if any */}
                    {inspectRun.detail?.sampleSnapshot && (
                      <div
                        style={{
                          padding: "12px 16px",
                          borderRadius: 8,
                          border: "1px solid var(--line)",
                          background: "var(--bg)",
                        }}
                      >
                        <div
                          style={{
                            margin: "0 0 6px",
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            color: "var(--muted)",
                          }}
                        >
                          Sample Output Reference Snapshot
                        </div>
                        <pre
                          style={{
                            background: "var(--panel)",
                            padding: 10,
                            borderRadius: 6,
                            border: "1px solid var(--line)",
                            fontSize: 12,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: 180,
                            overflowY: "auto",
                            margin: 0,
                          }}
                        >
                          {inspectRun.detail.sampleSnapshot}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 3: GENERATED CONTENT OUTPUT */}
                {inspectRun.activeTab === "content" && (
                  <div
                    style={{
                      padding: "16px",
                      borderRadius: 8,
                      border: "1px solid var(--line)",
                      background: "var(--bg)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <strong style={{ fontSize: 14, color: "var(--text)" }}>
                        Generated Content Preview
                      </strong>
                      {inspectRun.content && (
                        <button
                          className="btn mini"
                          onClick={() => {
                            navigator.clipboard.writeText(inspectRun.content || "");
                            alert("Content copied to clipboard!");
                          }}
                        >
                          📋 Copy Content
                        </button>
                      )}
                    </div>

                    {inspectRun.content ? (
                      <ContentPreview
                        content={inspectRun.content}
                        responseType={inspectRun.responseType ?? "html"}
                      />
                    ) : (
                      <div style={{ padding: "32px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                        No output content recorded for this run.
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 4: PIPELINE STEP GRAPH (FOR PIPELINES) */}
                {inspectRun.activeTab === "steps" && inspectRun.run.runType === "PIPELINE_EXECUTION" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {/* Steps Table */}
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: 8,
                        border: "1px solid var(--line)",
                        background: "var(--bg)",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 8px",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          color: "var(--muted)",
                        }}
                      >
                        Pipeline Execution Steps Graph
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                          <thead>
                            <tr style={{ background: "var(--panel)" }}>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>
                                Step #
                              </th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>Step Name</th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>Type</th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>Model</th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "right" }}>Cost ($)</th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>Status</th>
                              <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--line)", fontSize: 11, fontWeight: 700, color: "var(--muted)", textAlign: "center" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inspectRun.detail?.stepExecutions?.map((step: any) => {
                              const isSelected = inspectRun.selectedStepOrder === step.stepOrder;
                              return (
                                <tr
                                  key={step.id || step.stepOrder}
                                  style={{
                                    background: isSelected ? "var(--brand2)" : "transparent",
                                    borderBottom: "1px solid var(--line)",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => setInspectRun({ ...inspectRun, selectedStepOrder: step.stepOrder })}
                                >
                                  <td style={{ padding: "6px 10px", textAlign: "center", fontWeight: 800 }}>
                                    Step {step.stepOrder}
                                  </td>
                                  <td style={{ padding: "6px 10px", fontWeight: 700 }}>{step.stepName}</td>
                                  <td style={{ padding: "6px 10px", fontSize: 12 }}>
                                    <code>{step.stepType ?? "LLM"}</code>
                                  </td>
                                  <td style={{ padding: "6px 10px", fontSize: 12 }}>🤖 {step.model ?? "Default"}</td>
                                  <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>
                                    <CostDisplay cost={step.estimatedCost} size={13} />
                                  </td>
                                  <td style={{ padding: "6px 10px", textAlign: "center" }}>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        background: step.status === "COMPLETED" ? "var(--greenbg)" : step.status === "FAILED" ? "var(--redbg)" : "var(--amberbg)",
                                        color: step.status === "COMPLETED" ? "var(--green)" : step.status === "FAILED" ? "var(--red)" : "var(--amber)",
                                      }}
                                    >
                                      {step.status}
                                    </span>
                                  </td>
                                  <td style={{ padding: "6px 10px", textAlign: "center" }}>
                                    <button
                                      className="btn mini"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInspectRun({ ...inspectRun, selectedStepOrder: step.stepOrder });
                                      }}
                                    >
                                      {isSelected ? "Active" : "Inspect"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Selected Step Deep Dive */}
                    {(() => {
                      const selStep = inspectRun.detail?.stepExecutions?.find(
                        (s: any) => s.stepOrder === (inspectRun.selectedStepOrder ?? 1),
                      );
                      if (!selStep) return null;
                      return (
                        <div
                          style={{
                            padding: "14px 16px",
                            borderRadius: 8,
                            border: "2px solid var(--brand)",
                            background: "var(--bg)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  background: "var(--brand2)",
                                  color: "var(--brand)",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  display: "inline-block",
                                }}
                              >
                                Step #{selStep.stepOrder}
                              </span>
                              <h4 style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 800 }}>
                                {selStep.stepName}
                              </h4>
                            </div>
                            <CostDisplay cost={selStep.estimatedCost} size={15} />
                          </div>

                          {selStep.errorMessage && (
                            <Notice kind="error">{selStep.errorMessage}</Notice>
                          )}

                          {selStep.promptSnapshot && (
                            <div>
                              <span className="mini muted" style={{ fontWeight: 700, fontSize: 11 }}>
                                Step Prompt Snapshot:
                              </span>
                              <pre
                                style={{
                                  background: "var(--panel)",
                                  padding: 8,
                                  borderRadius: 6,
                                  border: "1px solid var(--line)",
                                  fontSize: 11,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  maxHeight: 160,
                                  overflowY: "auto",
                                  marginTop: 4,
                                  margin: 0,
                                }}
                              >
                                {selStep.promptSnapshot}
                              </pre>
                            </div>
                          )}

                          {selStep.outputContent && (
                            <div>
                              <span className="mini muted" style={{ fontWeight: 700, fontSize: 11 }}>
                                Step Generated Output:
                              </span>
                              <div style={{ marginTop: 4 }}>
                                <ContentPreview
                                  content={
                                    typeof selStep.outputContent === "string"
                                      ? selStep.outputContent
                                      : JSON.stringify(selStep.outputContent, null, 2)
                                  }
                                  responseType="html"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Modal Close Button */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 10,
                borderTop: "1px solid var(--line)",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn primary"
                onClick={handleCloseInspectRun}
                style={{ padding: "6px 20px", fontSize: 13, fontWeight: 700, borderRadius: 6, cursor: "pointer" }}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
